"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MessageCircleQuestionMark } from "lucide-react";
const categories = [{
  id: "consumer",
  title: "Consumer"
}, {
  id: "partner",
  title: "Partner"
}, {
  id: "technical",
  title: "Technical"
}];
const faqs = [{
  id: "c1",
  categoryId: "consumer",
  question: "How do I rent a powerbank?",
  answer: "Simply download the ZapGO! app, scan the QR code on the charging station, and the powerbank will pop out automatically. You can start charging immediately."
}, {
  id: "c2",
  categoryId: "consumer",
  question: "How much does it cost to rent?",
  answer: "Pricing is clearly displayed in the app before you confirm your rental. We charge a small fee per hour, with a maximum daily cap so you never overpay."
}, {
  id: "c3",
  categoryId: "consumer",
  question: "Where can I return the powerbank?",
  answer: "You can return the powerbank to any ZapGO! station with an available empty slot. Use the map in our app to find the nearest station to your current location."
}, {
  id: "p1",
  categoryId: "partner",
  question: "How do I become a venue partner?",
  answer: "Contact us through our website or app to apply. Our Business Development Lead will review your location and discuss the best partnership model for your venue."
}, {
  id: "p2",
  categoryId: "partner",
  question: "What are the benefits of hosting a station?",
  answer: "Hosting a ZapGO! station provides an additional revenue stream, increases foot traffic, and offers a valuable, modern convenience that keeps your customers happy and engaged longer."
}, {
  id: "p3",
  categoryId: "partner",
  question: "Is there an upfront cost to host?",
  answer: "We offer flexible partnership options, including zero-investment models for qualifying venues where we handle the installation and maintenance completely free of charge."
}, {
  id: "t1",
  categoryId: "technical",
  question: "What charging cables are included?",
  answer: "Every ZapGO! powerbank comes equipped with three built-in cables: Lightning (for Apple devices), USB-C, and Micro-USB, ensuring compatibility with almost any modern device."
}, {
  id: "t2",
  categoryId: "technical",
  question: "How fast do the powerbanks charge?",
  answer: "Our powerbanks support fast charging technology, delivering optimal power to get your device's battery level up quickly and safely while you are on the go."
}, {
  id: "t3",
  categoryId: "technical",
  question: "What if the powerbank I rented is defective?",
  answer: "If you encounter a broken or uncharged powerbank, please return it to the station immediately and report it via the app. You will not be charged for defective units."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="veelgestelde-vragen" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
            <MessageCircleQuestionMark className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Find answers to common questions about renting, partnering, and using
            ZapGO! powerbanks.
          </p>
        </div>

        <div ref={contentRef} className={`transition-all duration-700 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Tabs defaultValue="consumer" className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="grid w-full max-w-md grid-cols-3">
                {categories.map((category, i) => <TabsTrigger key={category.id} value={category.id} data-index={i}>
                    {category.title}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {categories.map((category, i) => <TabsContent key={category.id} value={category.id} className="mt-0 outline-none" data-index={i}>
                <div className="bg-card border border-border rounded-2xl p-4 md:p-6 shadow-sm">
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.filter(faq => faq.categoryId === category.id).map((faq, j) => <AccordionItem key={faq.id} value={faq.id} data-index={j} className="border-border">
                          <AccordionTrigger className="text-base md:text-lg font-semibold text-foreground hover:text-primary transition-colors">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>)}
                  </Accordion>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        <div className="mt-16 text-center bg-muted/50 rounded-2xl p-8 border border-border">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Still have questions?
          </h3>
          <p className="text-muted-foreground mb-6">
            Our support team is available 24/7 to help you with any issues.
          </p>
          <Button asChild size="lg" className="rounded-full px-8">
            <Link to="/SupportPage">Contact Support</Link>
          </Button>
        </div>
      </div>
    </section>;
}